import {
  configHash,
  guardrailsConfig,
  judgeAgrees,
  resultValue,
  rubricConfig,
  sanitizeJudgeConfig,
  type ExampleLine,
  type JudgeConfig,
  type JudgeExample,
  type JudgeExpected,
  type JudgeKind,
  type JudgeResult,
} from '../../utils/judges';
import type { ValidateRun, ValidateTurn } from './validate-runs';

/**
 * A solution's judges as Validate keeps them: each judge's prompt inputs
 * (`JudgeConfig`, from `utils/judges.ts`) plus where it runs, its version,
 * and the conversations the team scored to check it against.
 *
 * Kept per solution in this browser for now, beside the runs and suites
 * (`validate-store.ts`). Pure, so defaults, parsing and the agreement maths
 * are tested without a page.
 */

/** How many live conversations a judge scores. */
export type LiveSample = 'all' | '4' | '10';
/** Where a guardrail failure on a live conversation goes. */
export type LiveAlert = 'off' | 'slack' | 'email';

/** A conversation the team scored, to check the judge against. */
export interface TeamSample {
  id: string;
  /** The run it came from, when it still exists. */
  runId: string;
  title: string;
  /** The conversation as it was scored, so it outlives its run. */
  transcript: ValidateTurn[];
  scenario?: string;
  expectation?: string;
  /** What the team gave it: a score, "pass"/"fail", or "Topic › Sub". */
  team: JudgeExpected;
  /** What the judge gave it, and with which config (`configHash`). */
  judged?: { hash: string; result: JudgeResult; at: number };
  /** A rule that would make the judge agree, for the config it was asked for. */
  suggestion?: { hash: string; rule: string; why: string };
}

export interface ValidateJudge {
  id: string;
  config: JudgeConfig;
  description: string;
  version: number;
  updatedAt: number;
  /** Scores Test, scenarios and suites. Always on for the rubric judge. */
  tests: boolean;
  /** Scores live conversations, once live scoring is connected. */
  live: boolean;
  sample: LiveSample;
  alert: LiveAlert;
  samples: TeamSample[];
}

/** What a judge made of a run, kept on the run. */
export type RunJudgeEntry =
  | { status: 'done'; hash: string; result: JudgeResult; at: number }
  | { status: 'error'; hash: string; message: string };

export const MAX_JUDGES = 12;
export const MAX_SAMPLES = 40;
/** Turns kept per scored conversation; the end is what's scored. */
export const MAX_SAMPLE_TURNS = 40;

export const RUBRIC_JUDGE_ID = 'quality';

const DESCRIPTIONS: Record<JudgeKind, string> = {
  rubric:
    'Scores every reply and conversation on the Generative AI Evaluation Rubric. It’s what passes or fails a run.',
  score: 'Scores conversations on its own categories, out of 100.',
  check:
    'Fails a conversation outright for conduct, harmful advice or a leak. Passes unless a check clearly fails.',
  tag: 'Tags each conversation with a topic, a subtopic and how the customer felt.',
};

export const newJudge = (
  id: string,
  config: JudgeConfig,
  now: number
): ValidateJudge => ({
  id,
  config,
  description: DESCRIPTIONS[config.kind],
  version: 1,
  updatedAt: now,
  tests: config.kind !== 'tag',
  live: false,
  sample: 'all',
  alert: 'off',
  samples: [],
});

/** What every solution starts with: the rubric, and guardrails. */
export const defaultJudges = (now: number): ValidateJudge[] => [
  newJudge(RUBRIC_JUDGE_ID, rubricConfig(), now),
  newJudge('guardrails', guardrailsConfig(), now),
];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const parseSample = (v: unknown): TeamSample | null => {
  if (!isRecord(v) || typeof v.id !== 'string' || !Array.isArray(v.transcript))
    return null;
  const team =
    typeof v.team === 'number' || typeof v.team === 'string' ? v.team : null;
  if (team === null) return null;
  return {
    id: v.id,
    runId: typeof v.runId === 'string' ? v.runId : '',
    title: typeof v.title === 'string' ? v.title : 'Conversation',
    transcript: (v.transcript as ValidateTurn[]).slice(-MAX_SAMPLE_TURNS),
    ...(typeof v.scenario === 'string' ? { scenario: v.scenario } : {}),
    ...(typeof v.expectation === 'string'
      ? { expectation: v.expectation }
      : {}),
    team,
    ...(isRecord(v.judged) && isRecord(v.judged.result)
      ? { judged: v.judged as TeamSample['judged'] }
      : {}),
    ...(isRecord(v.suggestion)
      ? { suggestion: v.suggestion as TeamSample['suggestion'] }
      : {}),
  };
};

const SAMPLES: LiveSample[] = ['all', '4', '10'];
const ALERTS: LiveAlert[] = ['off', 'slack', 'email'];

export const parseJudge = (v: unknown): ValidateJudge | null => {
  if (!isRecord(v) || typeof v.id !== 'string') return null;
  const config = sanitizeJudgeConfig(v.config);
  if (!config) return null;
  return {
    id: v.id,
    config,
    description:
      typeof v.description === 'string'
        ? v.description.slice(0, 300)
        : DESCRIPTIONS[config.kind],
    version:
      typeof v.version === 'number' && v.version >= 1
        ? Math.round(v.version)
        : 1,
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
    tests: config.kind === 'rubric' ? true : v.tests !== false,
    live: v.live === true,
    sample: SAMPLES.includes(v.sample as LiveSample)
      ? (v.sample as LiveSample)
      : 'all',
    alert: ALERTS.includes(v.alert as LiveAlert)
      ? (v.alert as LiveAlert)
      : 'off',
    samples: Array.isArray(v.samples)
      ? v.samples
          .map(parseSample)
          .filter((x): x is TeamSample => x !== null)
          .slice(0, MAX_SAMPLES)
      : [],
  };
};

/**
 * Exactly one rubric judge, first: a solution with no judges saved gets the
 * defaults, and one whose rubric judge was lost gets it back.
 */
export const withRubric = (
  judges: ValidateJudge[],
  now: number
): ValidateJudge[] => {
  if (!judges.length) return defaultJudges(now);
  const rubric = judges.find((j) => j.config.kind === 'rubric');
  const rest = judges.filter((j) => j.config.kind !== 'rubric');
  return [rubric ?? newJudge(RUBRIC_JUDGE_ID, rubricConfig(), now), ...rest];
};

/**
 * What the score route should carry for the rubric judge: null while it's
 * the rubric as it comes (no rules, examples or edited criteria), so an
 * untouched solution sends exactly what it always did.
 */
export const scoringJudgeOf = (judges: ValidateJudge[]): JudgeConfig | null => {
  const rubric = judges.find((j) => j.config.kind === 'rubric')?.config;
  if (!rubric) return null;
  return rubric.rules.length || rubric.examples.length || rubric.criteria.length
    ? rubric
    : null;
};

/* ---- Agreement with the team --------------------------------------------- */

export interface Agreement {
  /** Conversations the team scored. */
  total: number;
  /** Of those, judged with the config as it is now. */
  checked: number;
  agree: number;
  /** Judged with an earlier config: check again to count them. */
  stale: number;
  /** Agree as a share of checked, 0–100; null before any are checked. */
  rate: number | null;
  /** Score judges: the mean gap to the team, in points. */
  gap: number | null;
  disagreements: TeamSample[];
}

export const agreement = (
  config: JudgeConfig,
  samples: TeamSample[]
): Agreement => {
  const hash = configHash(config);
  const current = samples.filter((s) => s.judged?.hash === hash);
  const disagreements = current.filter(
    (s) =>
      !judgeAgrees(
        config,
        s.team,
        (s.judged as NonNullable<TeamSample['judged']>).result
      )
  );
  const scored = config.kind === 'rubric' || config.kind === 'score';
  const gaps = scored
    ? current.map((s) =>
        Math.abs(Number(s.team) - Number(s.judged?.result.score ?? 0))
      )
    : [];
  const agree = current.length - disagreements.length;
  return {
    total: samples.length,
    checked: current.length,
    agree,
    stale: samples.length - current.length,
    rate: current.length ? Math.round((agree / current.length) * 100) : null,
    gap: gaps.length
      ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length)
      : null,
    disagreements,
  };
};

/** What the judge's result reads as beside the team's: "84", "Fail", "Billing › Autopay". */
export const valueText = (kind: JudgeKind, v: JudgeExpected): string =>
  kind === 'check' ? (v === 'pass' ? 'Pass' : 'Fail') : String(v);

/** Whether a value the team typed fits the judge: a 0–100 score, pass/fail, or a topic. */
export const parseTeamValue = (
  config: JudgeConfig,
  raw: string
): JudgeExpected | null => {
  const s = raw.trim();
  if (!s) return null;
  if (config.kind === 'check') {
    const v = s.toLowerCase();
    return v === 'pass' || v === 'fail' ? v : null;
  }
  if (config.kind === 'tag') {
    const [topic, sub] = s.split('›').map((x) => x.trim());
    const t = config.topics.find(
      (x) => x.name.toLowerCase() === topic.toLowerCase()
    );
    if (!t) return null;
    if (!sub) return t.name;
    const st = t.subs.find((x) => x.toLowerCase() === sub.toLowerCase());
    return st ? `${t.name} › ${st}` : null;
  }
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? Math.round(n) : null;
};

/* ---- From a run ------------------------------------------------------------ */

/** The conversation a judge reads: no platform lines, at most the last N turns. */
const judgedTurns = (run: ValidateRun) =>
  run.transcript.filter((t) => !t.system).slice(-MAX_SAMPLE_TURNS);

export const sampleFromRun = (
  id: string,
  run: ValidateRun,
  team: JudgeExpected
): TeamSample => ({
  id,
  runId: run.id,
  title: run.suite?.caseName || run.title,
  transcript: judgedTurns(run),
  ...(run.kind === 'scenario' ? { scenario: run.title } : {}),
  ...(run.expectation ? { expectation: run.expectation } : {}),
  team,
});

/** A worked example from a run: its last twelve lines, with the team's result. */
export const exampleFromRun = (
  id: string,
  run: ValidateRun,
  { expected, note }: { expected: JudgeExpected; note: string }
): JudgeExample => ({
  id,
  title: (run.suite?.caseName || run.title).slice(0, 80),
  lines: run.transcript
    .filter((t) => !t.system && !t.seeded && t.text.trim())
    .slice(-12)
    .map(
      (t): ExampleLine => ({
        who: t.role === 'bot' ? 'bot' : 'customer',
        text: t.text.slice(0, 600),
      })
    ),
  expected,
  note: note.trim().slice(0, 600),
});

/** A team sample as an example, with what the team gave it. */
export const exampleFromSample = (
  id: string,
  s: TeamSample,
  note: string
): JudgeExample => ({
  id,
  title: s.title.slice(0, 80),
  lines: s.transcript
    .filter((t) => !t.system && !t.seeded && t.text.trim())
    .slice(-12)
    .map((t) => ({
      who: t.role === 'bot' ? ('bot' as const) : ('customer' as const),
      text: t.text.slice(0, 600),
    })),
  expected: s.team,
  note: note.trim().slice(0, 600),
});

/** The judge's reasons, for the suggest route: failed checks, or criteria below their best. */
export const resultReasons = (r: JudgeResult): string[] =>
  [
    ...r.checks.filter((c) => c.failed).map((c) => `${c.name}: ${c.reason}`),
    ...r.criteria
      .filter((c) => c.reason)
      .map((c) => `${c.label} ${c.score}: ${c.reason}`),
  ].slice(0, 12);

export { resultValue };

/** Judges that score test runs besides the rubric (which scores them already). */
export const extraTestJudges = (judges: ValidateJudge[]) =>
  judges.filter((j) => j.config.kind !== 'rubric' && j.tests);

/** Bump the version and stamp the time: what Save does. */
export const savedJudge = (j: ValidateJudge, now: number): ValidateJudge => ({
  ...j,
  version: j.version + 1,
  updatedAt: now,
});
